/* =====================================================================================
   Technical profile panel — what a laboratory actually runs.

   Sits between the laboratory description and the commercial assessment, because that is
   the order the conversation happens in: what do you do, what do you do it with, and what
   would it take to change.

   Everything is optional and everything starts empty. This information is not reliably
   published, and a confident wrong equipment list is worse than a blank one.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import { MethodVendors } from './MethodVendors';
import { TechnicalHistory } from './TechnicalHistory';
import { Attachments } from './Attachments';
import {
  fetchTechnical, saveTechnical, toList, fromList, impliesLongRead, SUGGEST,
  CFDNA_TUBES,
  type Technical,
} from '../lib/technical';
import './technical.css';

function ListField({ label, value, onChange, suggest, placeholder }: {
  label: string; value: string; onChange: (v: string) => void;
  suggest?: readonly string[]; placeholder?: string;
}) {
  const id = `sug-${label.replace(/\W/g, '')}`;
  return (
    <label className="tp-field">
      <span>{label}</span>
      {/* A datalist suggests without constraining: type anything, but the common answers are
          one keystroke away. An enum here would need a migration every time a vendor ships
          a new box. */}
      <input list={suggest ? id : undefined} value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
      {suggest && (
        <datalist id={id}>{suggest.map((s) => <option key={s} value={s} />)}</datalist>
      )}
    </label>
  );
}

export function TechnicalPanel({ siteId, siteName }: { siteId: string; siteName: string }) {
  const [t, setT] = useState<Technical | null>(null);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    fetchTechnical(siteId).then((r) => {
      setT(r);
      setF({
        accreditations: fromList(r?.accreditations),
        hla_typing_methods: fromList(r?.hla_typing_methods),
        antibody_methods: fromList(r?.antibody_methods),
        dna_extraction: fromList(r?.dna_extraction),
        thermocyclers: fromList(r?.thermocyclers),
        sequencers: fromList(r?.sequencers),
        flow_cytometers: fromList(r?.flow_cytometers),
        qpcr_platforms: fromList(r?.qpcr_platforms),
        liquid_handlers: fromList(r?.liquid_handlers),
        hla_software: fromList(r?.hla_software),
        send_out_tests: fromList(r?.send_out_tests),
        other_equipment: fromList(r?.other_equipment),
        luminex_platform: r?.luminex_platform ?? '',
        digital_pcr: r?.digital_pcr ?? '',
        chimerism_method: r?.chimerism_method ?? '',
        lims: r?.lims ?? '',
        hla_resolution: r?.hla_resolution ?? '',
        annual_typings: r?.annual_typings?.toString() ?? '',
        annual_crossmatches: r?.annual_crossmatches?.toString() ?? '',
        turnaround_hours_routine: r?.turnaround_hours_routine?.toString() ?? '',
        turnaround_hours_urgent: r?.turnaround_hours_urgent?.toString() ?? '',
        staffing_wte: r?.staffing_wte?.toString() ?? '',
        source: r?.source ?? '',
        observed_on: r?.observed_on ?? new Date().toISOString().slice(0, 10),
        cfdna_tube: r?.cfdna_tube ?? '',
        cfdna_tube_other: r?.cfdna_tube_other ?? '',
        plasma_input_ml: r?.plasma_input_ml?.toString() ?? '',
        plasma_separation_hrs: r?.plasma_separation_hrs?.toString() ?? '',
        cfdna_extraction_kit: r?.cfdna_extraction_kit ?? '',
        capacity_note: r?.capacity_note ?? '',
        notes: r?.notes ?? '',
      });
    }).catch((e) => setErr(String(e.message ?? e)));
  }, [siteId]);

  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const seq = toList(f.sequencers ?? '');
  const longRead = impliesLongRead(seq);

  async function save() {
    setErr(null); setBusy(true); setOk(false);
    try {
      await saveTechnical(siteId, {
        accreditations: toList(f.accreditations), hla_typing_methods: toList(f.hla_typing_methods),
        antibody_methods: toList(f.antibody_methods), dna_extraction: toList(f.dna_extraction),
        thermocyclers: toList(f.thermocyclers), sequencers: seq,
        flow_cytometers: toList(f.flow_cytometers), qpcr_platforms: toList(f.qpcr_platforms),
        liquid_handlers: toList(f.liquid_handlers), hla_software: toList(f.hla_software),
        send_out_tests: toList(f.send_out_tests), other_equipment: toList(f.other_equipment),
        luminex_platform: f.luminex_platform || null, digital_pcr: f.digital_pcr || null,
        chimerism_method: f.chimerism_method || null, lims: f.lims || null,
        hla_resolution: f.hla_resolution || null,
        annual_typings: f.annual_typings ? Number(f.annual_typings) : null,
        annual_crossmatches: f.annual_crossmatches ? Number(f.annual_crossmatches) : null,
        turnaround_hours_routine: f.turnaround_hours_routine ? Number(f.turnaround_hours_routine) : null,
        turnaround_hours_urgent: f.turnaround_hours_urgent ? Number(f.turnaround_hours_urgent) : null,
        staffing_wte: f.staffing_wte ? Number(f.staffing_wte) : null,
        source: f.source || null, observed_on: f.observed_on || null,
        capacity_note: f.capacity_note || null, notes: f.notes || null,
        // Anything a person entered from a visit is verified; anything else stays unverified.
        confidence: f.source ? 'verified' : 'unverified',
      });
      setT(await fetchTechnical(siteId));
      setEditing(false); setOk(true);
      setTimeout(() => setOk(false), 3000);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  const stale = t?.observed_on
    && new Date(t.observed_on) < new Date(Date.now() - 548 * 86_400_000);

  return (
    <section className="site-block tp">
      <div className="tp-head">
        <h2>Technical</h2>
        <div className="tp-head-right">
          {t?.has_long_read && <span className="pill ok">long-read capable</span>}
          {stale && <span className="pill warn">last checked {t?.observed_on}</span>}
          <button className="link-btn" onClick={() => setEditing(!editing)}>
            {editing ? 'Cancel' : t ? 'Edit' : 'Record what they run'}
          </button>
        </div>
      </div>

      {!t && !editing && (
        <p className="block-lead">
          Nothing recorded for {siteName}. Equipment and methods are not reliably published, so
          this stays empty until someone who has been in the laboratory fills it in. A confident
          wrong equipment list costs more credibility than a blank one.
        </p>
      )}

      {t && !editing && (
        <>
          <div className="tp-grid">
            <Show k="Accreditation" v={fromList(t.accreditations)} />
            <Show k="HLA typing" v={fromList(t.hla_typing_methods)} />
            <Show k="Resolution" v={t.hla_resolution} />
            <Show k="Antibody" v={fromList(t.antibody_methods)} />
            <Show k="DNA extraction" v={fromList(t.dna_extraction)} />
            <Show k="Thermocyclers" v={fromList(t.thermocyclers)} />
            <Show k="Sequencers" v={fromList(t.sequencers)} strong={t.has_long_read ?? false} />
            <Show k="Flow cytometry" v={fromList(t.flow_cytometers)} />
            <Show k="Luminex" v={t.luminex_platform} />
            <Show k="qPCR" v={fromList(t.qpcr_platforms)} />
            <Show k="Digital PCR" v={t.digital_pcr} />
            <Show k="Liquid handling" v={fromList(t.liquid_handlers)} />
            <Show k="HLA software" v={fromList(t.hla_software)} />
            <Show k="LIMS" v={t.lims} />
            <Show k="Annual typings" v={t.annual_typings?.toLocaleString('en-GB')} />
            <Show k="Annual crossmatches" v={t.annual_crossmatches?.toLocaleString('en-GB')} />
            <Show k="Turnaround, routine" v={t.turnaround_hours_routine ? `${t.turnaround_hours_routine} h` : null} />
            <Show k="Staffing (WTE)" v={t.staffing_wte?.toString()} />
          </div>
          {/* What they send away is where the opening usually is. */}
          {t.send_out_tests?.length > 0 && (
            <p className="tp-sendout">
              <strong>Sent out:</strong> {fromList(t.send_out_tests)}
              <span className="muted">. Work leaving the building is work that could stay in it.</span>
            </p>
          )}
          {t.capacity_note && <p className="block-text small">{t.capacity_note}</p>}
          <p className="method-note">
            {t.source ? `Source: ${t.source}. ` : ''}
            {t.observed_on ? `Observed ${new Date(t.observed_on).toLocaleDateString('en-GB')}. ` : ''}
            Technical facts age quickly; anything over eighteen months old is flagged for re-checking.
          </p>
        </>
      )}

      {editing && (
        <div className="tp-edit">
          <div className="tp-section">Capability</div>
          <div className="tp-row">
            <ListField label="Accreditation" value={f.accreditations ?? ''} suggest={SUGGEST.accreditations}
                       onChange={(v) => set('accreditations', v)} placeholder="EFI, UKAS" />
            <ListField label="HLA typing methods" value={f.hla_typing_methods ?? ''} suggest={SUGGEST.hla_typing_methods}
                       onChange={(v) => set('hla_typing_methods', v)} placeholder="NGS, SSO" />
            <label className="tp-field"><span>Resolution</span>
              <select value={f.hla_resolution ?? ''} onChange={(e) => set('hla_resolution', e.target.value)}>
                <option value="">unknown</option>
                <option value="low">low</option><option value="intermediate">intermediate</option>
                <option value="high">high</option><option value="allele_level">allele level</option>
                <option value="mixed">mixed</option>
              </select>
            </label>
          </div>
          <div className="tp-row">
            <ListField label="Antibody methods" value={f.antibody_methods ?? ''} suggest={SUGGEST.antibody_methods}
                       onChange={(v) => set('antibody_methods', v)} />
            <ListField label="Luminex platform" value={f.luminex_platform ?? ''}
                       onChange={(v) => set('luminex_platform', v)} placeholder="LABScan 3D" />
            <ListField label="Chimerism method" value={f.chimerism_method ?? ''}
                       onChange={(v) => set('chimerism_method', v)} placeholder="STR, qPCR, NGS" />
          </div>

          <div className="tp-section">Equipment</div>
          <div className="tp-row">
            <ListField label="DNA extraction" value={f.dna_extraction ?? ''} suggest={SUGGEST.dna_extraction}
                       onChange={(v) => set('dna_extraction', v)} placeholder="QIAsymphony SP" />
            <ListField label="Thermocyclers" value={f.thermocyclers ?? ''} suggest={SUGGEST.thermocyclers}
                       onChange={(v) => set('thermocyclers', v)} />
          </div>
          <div className="tp-row">
            <ListField label="Sequencers" value={f.sequencers ?? ''} suggest={SUGGEST.sequencers}
                       onChange={(v) => set('sequencers', v)} placeholder="MiSeq, GridION" />
            <ListField label="qPCR" value={f.qpcr_platforms ?? ''} suggest={SUGGEST.qpcr_platforms}
                       onChange={(v) => set('qpcr_platforms', v)} />
            <ListField label="Digital PCR" value={f.digital_pcr ?? ''}
                       onChange={(v) => set('digital_pcr', v)} placeholder="QX200, QIAcuity" />
          </div>
          {longRead && (
            <p className="tp-flag">
              Long-read platform detected in that list. This laboratory could run a nanopore
              assay without a capital purchase, which is a different sale entirely.
            </p>
          )}
          <div className="tp-row">
            <ListField label="Flow cytometers" value={f.flow_cytometers ?? ''} suggest={SUGGEST.flow_cytometers}
                       onChange={(v) => set('flow_cytometers', v)} />
            <ListField label="Liquid handlers" value={f.liquid_handlers ?? ''} suggest={SUGGEST.liquid_handlers}
                       onChange={(v) => set('liquid_handlers', v)} />
            <ListField label="Other equipment" value={f.other_equipment ?? ''}
                       onChange={(v) => set('other_equipment', v)} />
          </div>

          <div className="tp-section">Operations</div>
          <div className="tp-row">
            <ListField label="HLA software" value={f.hla_software ?? ''} suggest={SUGGEST.hla_software}
                       onChange={(v) => set('hla_software', v)} />
            <ListField label="LIMS" value={f.lims ?? ''} onChange={(v) => set('lims', v)} />
            <ListField label="Sent out to others" value={f.send_out_tests ?? ''}
                       onChange={(v) => set('send_out_tests', v)} placeholder="dd-cfDNA, chimerism" />
          </div>
          <div className="tp-row">
            <ListField label="Annual typings" value={f.annual_typings ?? ''} onChange={(v) => set('annual_typings', v)} />
            <ListField label="Annual crossmatches" value={f.annual_crossmatches ?? ''} onChange={(v) => set('annual_crossmatches', v)} />
            <ListField label="Routine TAT (hours)" value={f.turnaround_hours_routine ?? ''} onChange={(v) => set('turnaround_hours_routine', v)} />
            <ListField label="Staffing WTE" value={f.staffing_wte ?? ''} onChange={(v) => set('staffing_wte', v)} />
          </div>

          <div className="tp-section">Provenance</div>
          <div className="tp-row">
            <ListField label="Source" value={f.source ?? ''} onChange={(v) => set('source', v)}
                       placeholder="Site visit, lab manager, published method" />
            <label className="tp-field"><span>Observed on</span>
              <input type="date" value={f.observed_on ?? ''} onChange={(e) => set('observed_on', e.target.value)} />
            </label>
          </div>
          {/* THE PRE-ANALYTICAL CHAIN. For cell-free DNA the tube is not a detail, it is the
              assay: a stabilised tube holds white cells intact for days, EDTA gives about six
              hours before genomic background swamps the donor fraction. A laboratory on EDTA
              with 1 mL of plasma cannot run the same assay as one on Streck with 4 mL,
              whatever sequencer they own. */}
          <fieldset className="tp-group">
            <legend>Cell-free DNA, pre-analytical</legend>
            <div className="tp-row">
              <label className="tp-field"><span>Collection tube</span>
                <select value={f.cfdna_tube ?? ''} onChange={(e) => set('cfdna_tube', e.target.value)}>
                  <option value="">Not recorded</option>
                  {CFDNA_TUBES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              {f.cfdna_tube === 'other' && (
                <label className="tp-field"><span>Which tube</span>
                  <input value={f.cfdna_tube_other ?? ''}
                         onChange={(e) => set('cfdna_tube_other', e.target.value)}
                         placeholder="Named, or the record is the same as not answering" />
                </label>
              )}
            </div>
            <div className="tp-row">
              <label className="tp-field"><span>Plasma input (mL)</span>
                <input type="number" step="0.1" min="0" max="20" value={f.plasma_input_ml ?? ''}
                       onChange={(e) => set('plasma_input_ml', e.target.value)} />
              </label>
              <label className="tp-field"><span>Time to plasma separation (h)</span>
                <input type="number" step="0.5" min="0" value={f.plasma_separation_hrs ?? ''}
                       onChange={(e) => set('plasma_separation_hrs', e.target.value)} />
              </label>
              <label className="tp-field"><span>cfDNA extraction kit</span>
                <input value={f.cfdna_extraction_kit ?? ''}
                       onChange={(e) => set('cfdna_extraction_kit', e.target.value)}
                       placeholder="QIAamp MinElute ccfDNA" />
              </label>
            </div>
            <p className="tp-note">
              Plasma volume sets the floor on achievable sensitivity independently of the
              sequencing platform, which is why it is captured alongside the tube.
            </p>
          </fieldset>

          <label className="tp-field"><span>Notes</span>
            <textarea rows={2} value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
          </label>

          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="composer-actions">
            <button className="btn-cancel" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
            <button className="btn-save" onClick={save} disabled={busy}>
              {busy ? 'Saving' : 'Save technical profile'}
            </button>
          </div>
        </div>
      )}

      {!editing && t && (
        <>
          <Show k="cfDNA collection tube"
                v={t.cfdna_tube === 'other' ? t.cfdna_tube_other
                   : CFDNA_TUBES.find((x) => x.key === t.cfdna_tube)?.label} strong />
          <Show k="Plasma input" v={t.plasma_input_ml ? `${t.plasma_input_ml} mL` : null} />
          <Show k="Time to plasma separation"
                v={t.plasma_separation_hrs ? `${t.plasma_separation_hrs} h` : null} />
          <Show k="cfDNA extraction kit" v={t.cfdna_extraction_kit} />
        </>
      )}

      {/* Who supplies each method, and when the account is free to switch. */}
      <MethodVendors siteId={siteId} />

      {/* The vendor deck, the tender schedule, the validation report. Named, dated and
          attributed, and openable without leaving the record. */}
      <Attachments
        objectType="site_technical" objectId={siteId} bucket="technical"
        label="Documents"
        accept={[
          '.ppt', '.pptx', 'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          '.doc', '.docx', 'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          '.xls', '.xlsx', 'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          '.pdf', 'application/pdf',
        ].join(',')}
      />

      {/* Written by database trigger, so it records changes made through any route. */}
      <TechnicalHistory siteId={siteId} />

      {ok && <p className="tp-ok">Saved.</p>}
    </section>
  );
}

function Show({ k, v, strong }: { k: string; v?: string | null; strong?: boolean }) {
  if (!v) return null;
  return (
    <div className="tp-show">
      <div className="tp-show-k">{k}</div>
      <div className={`tp-show-v ${strong ? 'strong' : ''}`}>{v}</div>
    </div>
  );
}
